import React, {
    Ref,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Keyboard, KeyboardAvoidingView, ScrollView, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { Edges, SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { DEFAULT_STRIPE_SIZE } from 'config/constants';
import { LANGUAGE_KEYS } from 'config/languageKeys';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import {
    selectAppState,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import { RBParsedRail } from 'store/railsBuilder/reducer.types';
import {
    selectSearchMenuConfig,
    selectSearchPageConfig,
} from 'store/railsBuilder/selectors';
import {
    endSentryTransaction,
    startSentryTransaction,
} from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';
import { ContentType } from 'services/data-manager/types';

import useScreenFocused from 'utils/hooks/useScreenFocused';
import useStripeParantalControl from 'utils/hooks/useStripeParantalControl';
import { isWeb } from 'utils/responsiveness';

import { RailLoadingStatus } from 'screens/Modular/types';

import GenericPinDialog from 'components/GenericPinDialog';
import Spinner from 'components/Spinner';
import Stripe from 'components/Stripe';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import SearchHeader from './components/SearchHeader';
import useChannelsSearch from './hooks/useChannelsSearch';
import useSearch from './hooks/useSearch';
import styles from './styles';
import { Provider, SearchOptions, SearchType } from './types';

const { SCREEN_SEARCH_NORESULTS, SCREEN_SEARCH_NORESULTS_BODY } = LANGUAGE_KEYS;
const { ACCESSIBILITY_SEARCH_RESULTS_FOUND } =
    LANGUAGE_KEYS.ACCESSIBILITY.SEARCH;

const SEARCH_TYPE_TO_CONTENT_TYPE: Record<
    | ContentType.SearchProgram
    | ContentType.SearchCatchup
    | ContentType.SearchVod,
    SearchType
> = {
    [ContentType.SearchProgram]: SearchType.PROGRAM,
    [ContentType.SearchCatchup]: SearchType.CATCHUP,
    [ContentType.SearchVod]: SearchType.VOD,
};

const CATCHUP_SEARCH_OPTIONS: SearchOptions = {
    excludeProvider: Provider.HBOMAX,
    onlyplayable: true,
};

const VOD_SEARCH_OPTIONS: SearchOptions = {
    excludeProvider: Provider.HBOMAX,
};

const SEARCH_TYPE_TO_SEARCH_OPTIONS: Record<
    | ContentType.SearchProgram
    | ContentType.SearchCatchup
    | ContentType.SearchVod,
    SearchOptions | undefined
> = {
    [ContentType.SearchProgram]: undefined,
    [ContentType.SearchCatchup]: CATCHUP_SEARCH_OPTIONS,
    [ContentType.SearchVod]: undefined,
};
const mockOnSetStripeLoadingStatus = () => {};
const edges: Edges = ['top'];

function Search({
    route: { key: routeKey },
    navigation,
}: {
    route: RouteProp<RouteNavigationParams, SCREEN.SEARCH>;
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.SEARCH>;
}) {
    const { t } = useTranslation();
    const handleSearchChannels = useChannelsSearch();
    const dispatch = useDispatch();
    const { screenFocused } = useScreenFocused();

    const searchMenuConfig = useSelector(selectSearchMenuConfig);
    const searchScreenConfig = useSelector(selectSearchPageConfig);
    const appState = useSelector(selectAppState);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const [searchText, setSearchText] = useState('');
    const [searchWasPressed, setSearchWasPressed] = useState(false);
    const [showNoResults, setShowNoResults] = useState(false);
    const [showChannels, setShowChannels] = useState(false);

    const resultNotFoundContainerRef = useRef<View | null>(null);
    const resultContainerRef = useRef<ScrollView | View | null>(null);

    const {
        showPinDialog,
        onShowPinDialog,
        onParentalContolDismiss,
        onParentalControlChecked,
        onSetParentalControlCheckedCallback,
    } = useStripeParantalControl();

    const neededContentTypes: {
        [key in ContentType]?: boolean;
    } = useMemo(() => {
        if (searchScreenConfig) {
            return searchScreenConfig.rails.reduce((acc, rail) => {
                if (rail.properties.ContentType) {
                    return { ...acc, [rail.properties.ContentType]: true };
                }

                return acc;
            }, {});
        }

        return {};
    }, [searchScreenConfig]);

    const limitByContentType: {
        [key in ContentType]?: number;
    } = useMemo(() => {
        if (searchScreenConfig) {
            return searchScreenConfig.rails.reduce((acc, rail) => {
                if (rail.properties.ContentType) {
                    const railLimit = Number(rail.properties.limit);
                    const stripeLimit =
                        railLimit && !Number.isNaN(railLimit)
                            ? railLimit
                            : DEFAULT_STRIPE_SIZE;

                    return {
                        ...acc,
                        [rail.properties.ContentType]: stripeLimit,
                    };
                }

                return acc;
            }, {});
        }

        return {};
    }, [searchScreenConfig]);

    const {
        searchResult: foundProgrammes,
        loading: programsLoading,
        handleInstantSearch: handleSearchPrograms,
    } = useSearch({
        searchQuery: neededContentTypes[ContentType.SearchProgram]
            ? searchText
            : '',
        searchType: SearchType.PROGRAM,
        searchOptions: { limit: limitByContentType[ContentType.SearchProgram] },
    });

    const {
        searchResult: foundCatchups,
        loading: catchupLoading,
        handleInstantSearch: handleSearchCatchup,
    } = useSearch({
        searchQuery: neededContentTypes[ContentType.SearchCatchup]
            ? searchText
            : '',
        searchType: SearchType.CATCHUP,
        searchOptions: {
            ...CATCHUP_SEARCH_OPTIONS,
            limit: limitByContentType[ContentType.SearchCatchup],
        },
    });

    const {
        searchResult: foundVods,
        loading: vodsLoading,
        handleInstantSearch: handleSearchVod,
    } = useSearch({
        searchQuery: neededContentTypes[ContentType.SearchVod]
            ? searchText
            : '',
        searchType: SearchType.VOD,
        searchOptions: {
            ...VOD_SEARCH_OPTIONS,
            limit: limitByContentType[ContentType.SearchVod],
        },
    });

    const handleToggleSearchMode = useCallback(() => {
        Keyboard.dismiss();
    }, []);

    const handleChangeSearchText = useCallback((value: string) => {
        setSearchText(value);
        setSearchWasPressed(false);
    }, []);

    const handleSubmitEditing = useCallback(() => {
        if (searchText.length >= 1) {
            handleToggleSearchMode();

            if (searchText.length <= 2) {
                setSearchWasPressed(true);

                handleSearchPrograms({
                    searchQuery: neededContentTypes[ContentType.SearchProgram]
                        ? searchText
                        : '',
                    searchType: SearchType.PROGRAM,
                    searchOptions: {
                        limit: limitByContentType[ContentType.SearchProgram],
                    },
                });
                handleSearchCatchup({
                    searchQuery: neededContentTypes[ContentType.SearchCatchup]
                        ? searchText
                        : '',
                    searchType: SearchType.CATCHUP,
                    searchOptions: {
                        ...CATCHUP_SEARCH_OPTIONS,
                        limit: limitByContentType[ContentType.SearchCatchup],
                    },
                });
                handleSearchVod({
                    searchQuery: neededContentTypes[ContentType.SearchVod]
                        ? searchText
                        : '',
                    searchType: SearchType.VOD,
                    searchOptions: {
                        ...VOD_SEARCH_OPTIONS,
                        limit: limitByContentType[ContentType.SearchCatchup],
                    },
                });
            }
        }
    }, [
        searchText,
        neededContentTypes,
        limitByContentType,
        handleToggleSearchMode,
        handleSearchPrograms,
        handleSearchCatchup,
        handleSearchVod,
    ]);

    const parsedSearchScreenConfig = useMemo<
        Omit<typeof searchScreenConfig, 'rails'> & {
            rails: RBParsedRail[];
        }
    >(
        () => ({
            ...searchScreenConfig,
            rails:
                searchScreenConfig?.rails.map<RBParsedRail>((rail) => {
                    const railLimit = Number(rail.properties.limit);
                    const stripeLimit =
                        railLimit && !Number.isNaN(railLimit)
                            ? railLimit
                            : DEFAULT_STRIPE_SIZE;

                    if (
                        searchText &&
                        (searchText.length >= 3 || searchWasPressed)
                    ) {
                        const displayName = t(rail.title || rail.name);

                        if (
                            rail.properties.ContentType ===
                            ContentType.ChannelItem
                        ) {
                            const foundChannels =
                                handleSearchChannels(searchText);

                            return {
                                ...rail,
                                title: displayName,
                                hasNext: foundChannels.length > stripeLimit,
                                sections: foundChannels.slice(0, stripeLimit),
                            };
                        }

                        if (
                            rail.properties.ContentType ===
                            ContentType.SearchProgram
                        ) {
                            return {
                                ...rail,
                                title: displayName,
                                hasNext:
                                    foundProgrammes.totalRecords > stripeLimit,
                                sections: foundProgrammes.results,
                            };
                        }

                        if (
                            rail.properties.ContentType ===
                            ContentType.SearchCatchup
                        ) {
                            return {
                                ...rail,
                                title: displayName,
                                hasNext:
                                    foundCatchups.totalRecords > stripeLimit,
                                sections: foundCatchups.results,
                            };
                        }

                        if (
                            rail.properties.ContentType ===
                            ContentType.SearchVod
                        ) {
                            return {
                                ...rail,
                                title: displayName,
                                hasNext: foundVods.totalRecords > stripeLimit,
                                sections: foundVods.results,
                            };
                        }
                    }

                    return rail as unknown as RBParsedRail;
                }) || [],
        }),
        [
            t,
            searchScreenConfig,
            searchText,
            foundProgrammes,
            foundCatchups,
            foundVods,
            handleSearchChannels,
            searchWasPressed,
        ],
    );

    const somethingFound = useMemo(
        () =>
            parsedSearchScreenConfig.rails.some(
                ({ sections }) => sections.length,
            ),
        [parsedSearchScreenConfig],
    );

    const isSearchLoading = programsLoading || catchupLoading || vodsLoading;

    useEffect(() => {
        if (searchText.length >= 3) {
            dispatch(startSentryTransaction(SENTRY_TRANSACTIONS.SearchAction));
        }
    }, [searchText]);

    useEffect(() => {
        if (somethingFound) {
            dispatch(endSentryTransaction(SENTRY_TRANSACTIONS.SearchAction));
        }
    }, [somethingFound]);

    useEffect(() => {
        if (
            (searchText.length >= 3 || searchWasPressed) &&
            !isSearchLoading &&
            !somethingFound
        ) {
            dispatch(endSentryTransaction(SENTRY_TRANSACTIONS.SearchAction));
        }
    }, [searchText, isSearchLoading, somethingFound, searchWasPressed]);

    // Prevent 'No Results' from showing up before loading
    useEffect(() => {
        if (
            !isSearchLoading &&
            (searchText.length >= 3 || searchWasPressed) &&
            !somethingFound
        ) {
            const timeout = setTimeout(() => {
                setShowNoResults(true);
            }, 10); // delaying a bit to prevent flickering

            return () => {
                clearTimeout(timeout);
            };
        } else {
            setShowNoResults(false);
        }
    }, [isSearchLoading, searchText, somethingFound, searchWasPressed]);

    // Prevent Channels from showing up before loading all
    useEffect(() => {
        if (!isSearchLoading && somethingFound) {
            const timeout = setTimeout(() => {
                setShowChannels(true);
            }, 5);

            return () => {
                clearTimeout(timeout);
            };
        } else {
            setShowChannels(false);
        }
    }, [isSearchLoading, searchText, somethingFound]);

    useEffect(() => {
        if (
            (screenReaderEnabled || isWeb) &&
            (searchText.length >= 3 || searchWasPressed)
        ) {
            if (!isSearchLoading) {
                setTimeout(() => {
                    setAccessibilityFocusTo(
                        somethingFound
                            ? resultContainerRef
                            : resultNotFoundContainerRef,
                    );
                }, 200);
            }
        }
    }, [
        isSearchLoading,
        somethingFound,
        searchText,
        searchWasPressed,
        screenReaderEnabled,
        t,
    ]);

    const handleGoBack = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else if (isWeb) {
            window.history.back();
        }
    }, [navigation]);

    if (!searchMenuConfig) {
        return null;
    }

    return (
        <>
            <SafeAreaView style={styles.root} edges={edges}>
                <SearchHeader
                    searchText={searchText}
                    onChangeSearchText={handleChangeSearchText}
                    onSubmitEditing={handleSubmitEditing}
                    onCancelPress={handleGoBack}
                />
            </SafeAreaView>
            <KeyboardAvoidingView
                style={styles.keyboardAvoiding}
                behavior="padding"
                onTouchStart={handleToggleSearchMode}
            >
                {isSearchLoading && (
                    <View style={styles.loadingContainer}>
                        <Spinner routeKey={routeKey} />
                    </View>
                )}

                <View
                    ref={resultContainerRef as Ref<View>}
                    style={{ width: 1, height: 1 }}
                    accessible
                    importantForAccessibility={
                        somethingFound ? 'yes' : 'no-hide-descendants'
                    }
                    accessibilityElementsHidden={!somethingFound}
                    tabIndex={somethingFound ? 0 : -1}
                    accessibilityLabel={t(ACCESSIBILITY_SEARCH_RESULTS_FOUND)}
                    focusable
                />

                {!isSearchLoading && somethingFound && showChannels && (
                    <ScrollView
                        keyboardDismissMode="on-drag"
                        contentContainerStyle={
                            !!searchText &&
                            somethingFound &&
                            styles.bottomIndent
                        }
                        nestedScrollEnabled
                    >
                        {parsedSearchScreenConfig.rails.map((rail, index) => {
                            return (
                                <Stripe
                                    key={rail.id}
                                    rail={rail}
                                    templateId={searchMenuConfig?.id}
                                    navigation={navigation}
                                    searchQuery={searchText}
                                    searchOptions={
                                        SEARCH_TYPE_TO_SEARCH_OPTIONS[
                                            rail.properties.ContentType as
                                                | ContentType.SearchProgram
                                                | ContentType.SearchCatchup
                                                | ContentType.SearchVod
                                        ]
                                    }
                                    searchType={
                                        SEARCH_TYPE_TO_CONTENT_TYPE[
                                            rail.properties.ContentType as
                                                | ContentType.SearchProgram
                                                | ContentType.SearchCatchup
                                                | ContentType.SearchVod
                                        ]
                                    }
                                    loadingStatus={
                                        rail.sections.length
                                            ? RailLoadingStatus.LOADED
                                            : RailLoadingStatus.EMPTY
                                    }
                                    screenFocused={screenFocused}
                                    screenReaderEnabled={screenReaderEnabled}
                                    onSetStripeLoadingStatus={
                                        mockOnSetStripeLoadingStatus
                                    }
                                    onShowPinDialog={onShowPinDialog}
                                    onSetParentalControlCheckedCallback={
                                        onSetParentalControlCheckedCallback
                                    }
                                    index={index}
                                />
                            );
                        })}
                    </ScrollView>
                )}

                {(searchText.length >= 3 || searchWasPressed) &&
                    !isSearchLoading &&
                    !somethingFound &&
                    showNoResults && (
                        <View
                            ref={resultNotFoundContainerRef}
                            focusable
                            accessible
                            style={styles.noResultsContainer}
                        >
                            <Text
                                center
                                category={ETextCategory.title2}
                                color={ETextColor.light_grey}
                                accessibilityRole="text"
                            >
                                {t(SCREEN_SEARCH_NORESULTS)}
                            </Text>
                            <Text
                                center
                                category={ETextCategory.body1}
                                color={ETextColor.light_grey}
                                accessibilityRole="text"
                            >
                                {t(SCREEN_SEARCH_NORESULTS_BODY)}
                            </Text>
                        </View>
                    )}
            </KeyboardAvoidingView>
            <View>
                <GenericPinDialog
                    modal
                    appState={appState}
                    visible={showPinDialog}
                    onClose={onParentalContolDismiss}
                    onSuccess={onParentalControlChecked}
                />
            </View>
        </>
    );
}

export default Search;
